/**
 * SeasonShell - sticky top nav layout for all season pages.
 *
 * Renders:
 *   - top: League name + season-week badge, center tabs, right side day/night + switcher + user
 *   - children below
 *
 * Inherits day/night via ThemeContext.
 */
import { useEffect, useState } from "react";
import { useParams, NavLink, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Sun, Moon, Trophy, ArrowLeft, Calendar } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth, authHeaders } from "../../contexts/AuthContext";
import { useSeason } from "../../contexts/SeasonContext";
import TeamSwitcher from "./TeamSwitcher";
import ProfileButton from "../ProfileButton";
import AdminButton from "../AdminButton";
// [Session C-B CP-5] Global player search - same overlay used on the
// dashboard, now available inside every SeasonShell page too.
import PlayerSearchOverlay from "../dashboard/PlayerSearchOverlay";
import { Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { to: "home", label: "League" },
  { to: "team", label: "Team" },
  { to: "matchup", label: "Matchup" },
  { to: "players", label: "Players" },
  { to: "waivers", label: "Waivers" },
  { to: "trades", label: "Trades" },
  { to: "standings", label: "Standings" },
  { to: "settings", label: "Settings" },
];

export default function SeasonShell({ children }) {
  const { id: leagueId } = useParams();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { actingAsTeamId } = useSeason();
  const navigate = useNavigate();

  const [season, setSeason] = useState(null);
  const [leagueName, setLeagueName] = useState("");
  const [myTeamId, setMyTeamId] = useState(null);
  // [Session C-B CP-5] Global search overlay open/close.
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    // `season` is page-blocking - fail loudly. `leagueName` is optional (defaults to "").
    axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() })
      .then((r) => {
        setSeason(r.data);
        const t = (r.data?.teams || []).find((x) => x.id === actingAsTeamId)
              || (r.data?.teams || []).find((x) => x.user_id === user?.id)
              || (r.data?.teams || [])[0];
        setMyTeamId(t?.id || null);
      })
      .catch((_e) => setSeason(null));
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => setLeagueName(r.data?.name || ""))
      .catch((_e) => setLeagueName(""));
  }, [leagueId, actingAsTeamId, user]);

  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const headerBg = isDark ? "bg-[#111111]/85" : "bg-[#F0F0F0]/85";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  const tabClass = (active) =>
    `px-3 py-1.5 rounded-lg text-xs font-display font-bold uppercase tracking-wider transition-all ${
      active
        ? "bg-[#FF6B00]/15 text-[#FF6B00]"
        : `${muted} hover:${fg}`
    }`;

  const tabHref = (tab) => {
    if (tab.to === "team")    return `/league/${leagueId}/team/${myTeamId || ""}`;
    if (tab.to === "matchup") return `/league/${leagueId}/matchup/${season?.current_week || 1}`;
    return `/league/${leagueId}/${tab.to}`;
  };

  return (
    <div className={`min-h-screen ${bg} ${fg}`} data-testid="season-shell">
      <div className={`sticky top-0 z-40 backdrop-blur-md ${headerBg} border-b ${cardBorder}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link to="/dashboard" className={`${muted} hover:${fg}`} data-testid="shell-back-dashboard">
            <ArrowLeft size={16} />
          </Link>
          <Trophy size={16} className="text-[#FF6B00]" />
          <div className="font-display font-black uppercase tracking-wider text-sm truncate max-w-[180px] sm:max-w-xs">
            {leagueName || "League"}
          </div>
          {season && (
            <div className={`hidden md:flex items-center gap-1 text-[10px] uppercase tracking-widest ${muted} ml-1`}>
              <Calendar size={11} />
              <span>Week {season.current_week} / {season.regular_season_weeks} · {season.season_year}</span>
            </div>
          )}

          <div className="flex-1" />

          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((tab) => (
              <NavLink key={tab.to} to={tabHref(tab)} className={({ isActive }) => tabClass(isActive)} data-testid={`shell-tab-${tab.to}`}>
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <TeamSwitcher />
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg border ${cardBorder} hover:bg-[#FF6B00]/10`}
              data-testid="shell-theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            {/* [Phase 2a.2-B §4.7 R8a] Inline Profile button */}
            <ProfileButton variant={isDark ? "dark" : "light"} />
            <AdminButton variant={isDark ? "dark" : "light"} />
            {user && (
              <button onClick={logout} className={`hidden sm:block text-xs ${muted} hover:${fg}`} data-testid="shell-logout">
                Sign out
              </button>
            )}
          </div>
        </div>
        {/* Mobile tabs */}
        <div className="md:hidden flex overflow-x-auto px-3 pb-2 gap-1">
          {TABS.map((tab) => (
            <NavLink key={tab.to} to={tabHref(tab)} className={({ isActive }) => tabClass(isActive)} data-testid={`shell-tab-mobile-${tab.to}`}>
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </div>

      {/* [Session C-B CP-5] Global search overlay - one instance per shell. */}
      <PlayerSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <p className={`text-[10px] ${muted} text-center pb-6 px-4 max-w-2xl mx-auto leading-relaxed`}>
        Pregame Fantasy is not affiliated with the NFL or any team. Player names are used as factual
        informational data per First Amendment informational use framework.
      </p>
    </div>
  );
}
